import React, {useEffect, useState} from 'react';
import EmptyView from './empty-view';
import Api from '../services/api';
import PaginationNova from './pagination-nova';
import DepositTransactionModel from '../models/depositTransaction';
import FullScreenLoader from './full-screen';
import {DepositLastStatusEnum} from '../enums/depositLastStatusEnum';
import DepositAllStatus from '../models/depositAllStatus';
import DepositAllStatusValue from '../models/depositAllStatusValue';
import './scss/timeline.scss';
import DepositTransactionProp from './properties/deposit-transaction-prop';
import ConfirmDialog from './confirm-dialog';
import SelectBlockchain from './select-blockchains';

function DepositTransactions({setStatusMessage}: DepositTransactionProp): React.ReactElement {
    const [depositList, setDepositList] = useState([]);
    const [selectedChain, setSelectedChain] = useState('');
    const [txType, setTxType] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(10);
    const [itemCount, setItemCount] = useState(10);
    const [resetPaging, setResetPaging] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmDialogData, setConfirmDialogData] = useState({});

    const setFailedRefundStatusWithMessage = (transactions: []) => {
        transactions.forEach((transaction: any) => {
            const tx: DepositTransactionModel = transaction;

            let {lastStatus} = transaction;

            if (lastStatus && lastStatus.length > 0) {
                lastStatus = lastStatus[0].toUpperCase() + lastStatus.slice(1);
                tx.lastStatus = DepositLastStatusEnum[lastStatus as keyof typeof DepositLastStatusEnum];
            }

            const txAllStatus: DepositAllStatus[] = [];
            let forwardingHashFound = true;

            if (transaction.allStatus) {
                // Previous loaded transactions status are maintained, no required to process them again
                if (!Array.isArray(transaction.allStatus)) {
                    Object.entries(transaction.allStatus).forEach(([key, value]) => {
                        let statusKey: string = key as string;
                        let status: any;
                        if (statusKey && statusKey.length > 0) {
                            statusKey = statusKey[0].toUpperCase() + statusKey.slice(1);
                            status = DepositLastStatusEnum[statusKey as keyof typeof DepositLastStatusEnum];
                        }
                        const allStatus: DepositAllStatus = {
                            status,
                            value: value as DepositAllStatusValue,
                        };

                        txAllStatus.push(allStatus);
                    });

                    tx.allStatus = txAllStatus;
                }

                const txAllStatusList = transaction.allStatus as DepositAllStatus[];

                txAllStatusList.forEach((statusVal) => {
                    if (statusVal.status === DepositLastStatusEnum.Forwarding) {
                        if (!statusVal.value.txHash) {
                            forwardingHashFound = false;
                        }
                    }
                });
            }

            tx.failedStatus = transaction.lastStatus !== DepositLastStatusEnum.Complete;
            tx.refundStatus = !forwardingHashFound
                && (transaction.lastStatus === DepositLastStatusEnum.Forwarding
                    || transaction.lastStatus === DepositLastStatusEnum.ForwardingFailed);
            tx.retryStatus = tx.failedStatus;
        });
        setDepositList(transactions);
    };

    const getTransactions = (blockchain: string, type: string, limit: number, offset: number) => {
        setIsLoading(true);
        setStatusMessage('', '');
        Api.getDeposit(blockchain, type, limit, offset).then((result) => {
            setItemCount(result?.data?.length);
            if (result.length > pageCount) result.splice(-1);
            setFailedRefundStatusWithMessage(result?.data);
            setIsLoading(false);
        }).catch((error) => {
            console.error(error.message);
            setIsLoading(false);
        });
    };

    const handlePageClick = (pageSize: number, pageNumber: number) => {
        setCurrentPage(pageNumber);
        setPageCount(pageSize);
    };

    const handleTabClick = (event: any, data: string) => {
        event.preventDefault();
        if (txType === data) return;
        setDepositList([]);
        setTxType(data);
        setResetPaging(true);
    };

    const handlePostRefundRetryClick = (
        address: string,
        txHash: string,
        blockchain: string,
        refund: boolean,
    ) => {
        setConfirmDialogData({
            address,
            txHash,
            blockchain,
            refund,
        });
        if (refund) {
            setConfirmDialogTitle('Confirm Refund!');
            setConfirmDialogMessage(`Are you sure you want to refund transaction. Address: ${address}, Tx: ${txHash}, Blockchain:${blockchain}`);
        } else {
            setConfirmDialogTitle('Confirm Reprocess!');
            setConfirmDialogMessage(`Are you sure you want to reprocess transaction. Tx: ${txHash}, Blockchain: ${blockchain}`);
        }

        setShowConfirmDialog(true);
    };

    const handlePostRefund = (address: string, txHash: string, blockchain: string) => {
        setIsLoading(true);
        setStatusMessage('', '');
        Api.refundDeposit(address, txHash, blockchain).then((result) => {
            if (result.error) {
                if (result.error.message?.refund?.length > 0) {
                    setStatusMessage(result.error.message?.refund.toString(), 'error');
                } else {
                    setStatusMessage(result.error.message, 'error');
                }
            } else {
                setStatusMessage('Refund Success', 'success');
            }
            setIsLoading(false);
            window.scrollTo(0, 0);
        }).catch((error) => {
            setStatusMessage(error.message, 'error');
            setIsLoading(false);
            window.scrollTo(0, 0);
        });
    };

    const handlePostRetry = (address: string, txHash: string, blockchain: string) => {
        setIsLoading(true);
        setStatusMessage('', '');
        Api.startDeposit(address, txHash, blockchain).then((result) => {
            if (result.error) {
                if (result.error.message?.refund?.length > 0) {
                    setStatusMessage(result.error.message?.refund.toString(), 'error');
                } else {
                    setStatusMessage(result.error.message, 'error');
                }
            } else {
                setStatusMessage('Reprocess Success', 'success');
            }
            setIsLoading(false);
            window.scrollTo(0, 0);
        }).catch((error) => {
            setStatusMessage(error.message, 'error');
            setIsLoading(false);
            window.scrollTo(0, 0);
        });
    };

    const handleYesConfirmDialog = (data: any) => {
        setShowConfirmDialog(false);
        console.log(data);
        if (data.refund) {
            handlePostRefund(data.address, data.txHash, data.blockchain);
        } else {
            handlePostRetry(data.address, data.txHash, data.blockchain);
        }
    };

    useEffect(() => {
        if (selectedChain.length > 0) {
            getTransactions(
                selectedChain,
                txType,
                pageCount + 1,
                (currentPage - 1) * pageCount,
            );
        }
        setResetPaging(false);
    }, [currentPage, pageCount, txType]);

    return (
        <div className="tab-container-one p-3 loading-wrapper">
            <ConfirmDialog
                title={confirmDialogTitle}
                message={confirmDialogMessage}
                show={showConfirmDialog}
                onHide={() => {
                    setShowConfirmDialog(false);
                }}
                data={confirmDialogData}
                handleYes={handleYesConfirmDialog}
                handleNo={() => {
                    setShowConfirmDialog(false);
                }}
            />
            <div className="row">
                <div className="col-6">
                    <h4 className="mb-4">Deposit Transactions</h4>
                </div>
                <div className="col-6">
                    <a
                        href="/deposit/?action=start"
                        type="button"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Start deposit on an address"
                        className="btn btn-dark float-end"
                    >
                        Deposit To Address
                    </a>
                </div>
            </div>
            <ul className="nav nav-tabs" id="depositTab" role="tablist">
                <li className={`nav-item ${txType === 'all' ? 'active' : ''}`}>
                    <a
                        className={`nav-link ${txType === 'all' ? 'active' : ''}`}
                        href="#tx"
                        role="tab"
                        aria-controls="tx-tab"
                        data-bs-toggle="tab"
                        onClick={((e) => handleTabClick(e, 'all'))}
                    >
                        All
                    </a>
                </li>
                <li className={`nav-item ${txType === 'failed' ? 'active' : ''}`}>
                    <a
                        className={`nav-link ${txType === 'failed' ? 'active' : ''}`}
                        href="#tx"
                        role="tab"
                        aria-controls="tx-tab"
                        data-bs-toggle="tab"
                        onClick={((e) => handleTabClick(e, 'failed'))}
                    >
                        Failed
                    </a>
                </li>
            </ul>
            <div className="tab-content p-3" style={{backgroundColor: '#f4f4f4'}}>
                <div className="tab-pane active" id="tx" role="tabpanel" aria-labelledby="tx-tab">
                    <h5 className="pt-3 pb-2 d-inline-block">
                        List of
                        {' '}
                        {txType}
                        {' '}
                        transactions (
                        {selectedChain}
                        )
                    </h5>
                    <div className="d-inline-block float-end">
                        <SelectBlockchain
                            balor={true}
                            onLoading={() => {
                                setIsLoading(true);
                            }}
                            onLoaded={() => {
                            }}
                            onChange={(data) => {
                                setSelectedChain(data);
                                getTransactions(
                                    data,
                                    txType,
                                    pageCount + 1,
                                    (currentPage - 1) * pageCount,
                                );
                            }}
                            setStatusMessage={setStatusMessage}
                        />
                    </div>
                    <div className={`accordion tab-${txType}`} id="depositTxs">
                        {isLoading ? <FullScreenLoader/> : ''}
                        {(depositList?.length > 0)
                            ? depositList?.map((deposit: DepositTransactionModel, index) => (
                                <div
                                    key={deposit.txHash}
                                    className="accordion-item mb-2 border-1"
                                    style={{borderTop: '1px solid #dfdfdf'}}
                                >
                                    <h2
                                        className={`accordion-header ${deposit.lastStatus === DepositLastStatusEnum.Complete ? 'complete' : 'failed'}`}
                                        id={`deposit-heading-${index}`}
                                    >
                                        <button
                                            className="accordion-button d-block collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#deposit-${index}`}
                                            aria-expanded="false"
                                            aria-controls={`deposit-${index}`}
                                        >
                                            <span className="address me-2">Hash:</span>
                                            <span title={deposit.txHash} className="address" style={{maxWidth: '50%'}}>
                        {deposit.txHash}
                      </span>
                                            <span className="float-end fst-italic small">{deposit.date}</span>
                                        </button>
                                    </h2>
                                    <div
                                        id={`deposit-${index}`}
                                        className="accordion-collapse collapse"
                                        style={{backgroundColor: '#fff'}}
                                        aria-labelledby={`deposit-heading-${index}`}
                                    >
                                        <div className="row p-3">
                                            <div className="col-md-6">
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Tx Hash
                                                    </div>
                                                    <div className="col-9 address-100" title={deposit.txHash}>
                                                        {deposit.txHash}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Blockchain
                                                    </div>
                                                    <div className="col-9 address-100">
                                                        {deposit.blockchain}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Contract
                                                    </div>
                                                    <div className="col-9 address-100">
                                                        {deposit.contract}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Source
                                                    </div>
                                                    <div className="col-9 address-100">
                                                        {deposit.source}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Destination
                                                    </div>
                                                    <div className="col-9 address-100">
                                                        {deposit.destination}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Quantity
                                                    </div>
                                                    <div className="col-9 address-100">
                                                        {deposit.quantity}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col-3">
                                                        Last Status
                                                    </div>
                                                    <div className="col-9 address-100">
                                                        {deposit.lastStatus}
                                                    </div>
                                                </div>
                                                <div className="row pb-2 ps-3 pt-3">
                                                    <div className="col">
                                                        {deposit.refundStatus ? (
                                                            <a
                                                                href="#refund"
                                                                type="button"
                                                                data-bs-toggle="tooltip"
                                                                data-bs-placement="top"
                                                                title="Initiate Refund"
                                                                className="btn btn-dark me-2"
                                                                onClick={() => handlePostRefundRetryClick(
                                                                    deposit.source,
                                                                    deposit.txHash,
                                                                    deposit.blockchain,
                                                                    true,
                                                                )}
                                                            >
                                                                Refund
                                                            </a>
                                                        ) : ''}

                                                        {deposit.retryStatus ? (
                                                            <a
                                                                href="#retry"
                                                                type="button"
                                                                data-bs-toggle="tooltip"
                                                                data-bs-placement="top"
                                                                title="Reprocess transaction"
                                                                className="btn btn-dark me-2"
                                                                onClick={() => handlePostRefundRetryClick(
                                                                    deposit.destination,
                                                                    deposit.txHash,
                                                                    deposit.blockchain,
                                                                    false,
                                                                )}
                                                            >
                                                                Reprocess
                                                            </a>
                                                        ) : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            {deposit.allStatus ? (
                                                <div className="col-md-6">
                                                    <ul className="timeline mt-3 ms-3">
                                                        {Object.entries(deposit.allStatus).map((value: any, counter) => (
                                                            <li className="timeline-item mb-3">
                                                                <p className="title mb-1">{deposit.allStatus[counter].status}</p>
                                                                <p
                                                                    className=" address w-100 mb-0"
                                                                    title={deposit.allStatus[counter].value.txHash}
                                                                >
                                                                    {deposit.allStatus[counter].value.txHash ? deposit.allStatus[counter].value.txHash : ' - '}
                                                                </p>
                                                                <p className=" mb-3 date ">{deposit.allStatus[counter].value.date}</p>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : ''}
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="p-2" style={{backgroundColor: '#fff'}}>
                                    {!isLoading ? (
                                            <EmptyView
                                                title="Transactions not found!"
                                                desc="Seems like there are no transactions in database"
                                            />
                                        )
                                        : <EmptyView title="Loading.." desc=""/>}
                                </div>
                            )}
                    </div>
                </div>
            </div>
            <div className={"mt-4 mb-4"}>
                <PaginationNova itemCount={itemCount} onPageClick={handlePageClick} reset={resetPaging}/>
            </div>
            <div className=" row legend mb-4">
                <h6 className="pb-2">Legend</h6>
                <div className="col-1">
                    <div className="color-box success "/>
                    <div className="pt-1">Success</div>
                </div>
                <div className="col-1">
                    <div className="color-box failed "/>
                    <div className="pt-1">Failed</div>
                </div>
            </div>
        </div>
    );
}

export default DepositTransactions;
